import { OnDataOptions, useQuery, useSubscription } from '@apollo/client'
import { cache } from '@lib/graphql/cache'
import {
  ALL_ACCOUNTS,
  ALL_TEAMS,
  GET_USER_ACCOUNT,
} from '@lib/graphql/queries/Organization'
import {
  ACCOUNTS_SUBSCRIPTION,
  NOTIFICATIONS_COUNTER_SUBSCRIPTION,
  NOTIFICATIONS_SUBSCRIPTION,
  THREAD_NOTIFICATION_SUBSCRIPTION,
} from '@lib/graphql/subscriptions/Shared'
import {
  accountsSubscriptionDataHandler,
  newNotificationSubscriptionDataHandler,
} from '@lib/grid/subscriptions/subscriptionsEventHandlers'
import { isPresignedUrlExpiring } from '@mono/packages/utils/attachments'
import useTranslations from '@translations/useTranslations'
import { renderNotification } from '@utils/notifications/notifications'
import { useEffect, useRef } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import useLogout from './useLogout'
import useUser from './useUser'

const useSharedData = () => {
  const { t } = useTranslations()
  const { account } = useUser()
  const threadNotificationSound = useRef(null)
  const { logoutClient } = useLogout()

  const { refetch: refetchAllAccounts } = useQuery(ALL_ACCOUNTS, {
    fetchPolicy: 'network-only',
    pollInterval: 1000 * 60 * 60 * 2,
  })

  useQuery(ALL_TEAMS, {
    pollInterval: 1000 * 60 * 60 * 24,
  })

  useEffect(() => {
    if (typeof document === 'undefined') return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const query = cache.readQuery({
          query: GET_USER_ACCOUNT,
        })
        const profileImage = query?.getCurrentUserAccount?.profileImage
        const sampleUrl =
          profileImage?.thumbnailUrl ?? profileImage?.presignedUrl

        if (isPresignedUrlExpiring(sampleUrl)) {
          refetchAllAccounts()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refetchAllAccounts])

  const handleBrowserNotification = (options: OnDataOptions<any>) => {
    const notification = options.data.data?.notifications

    if (notification) {
      const notificationBody = renderNotification({
        data: notification,
        t,
      })

      if (notificationBody !== null) {
        const html = renderToStaticMarkup(notificationBody)
        const doc = new DOMParser().parseFromString(html, 'text/html')

        if (doc?.body?.textContent) {
          const body = doc?.body?.textContent

          new Notification('Havelock', {
            body,
            icon: '/favicon-default.ico',
            tag: notification?.id,
          })
        }
      }
    }
  }

  const handleNotificationSound = () => {
    if (!account?.id || !account?.playThreadNotificationSound) return

    threadNotificationSound.current?.play()
  }

  useSubscription(ACCOUNTS_SUBSCRIPTION, {
    onData: (options) =>
      accountsSubscriptionDataHandler(account, options, () => {
        logoutClient({
          logoutParams: {
            returnTo: window.location.origin,
          },
        })
      }),
  })

  useSubscription(NOTIFICATIONS_SUBSCRIPTION, {
    onData: (options) => {
      handleBrowserNotification(options)
    },
  })

  useSubscription(NOTIFICATIONS_COUNTER_SUBSCRIPTION, {
    onData: (options) => {
      return newNotificationSubscriptionDataHandler(options)
    },
  })

  useSubscription(THREAD_NOTIFICATION_SUBSCRIPTION, {
    onData: handleNotificationSound,
  })

  useEffect(() => {
    threadNotificationSound.current = new Audio(
      '/thread-notification-sound.mp3',
    )
  }, [])
}

export default useSharedData
